import { IMulticonsultUser } from "@mctypes/backendTypes";
import { IMulticonsultProject } from "@mctypes/global.types";
import * as R from "ramda";
import { IAkustikkPremissRapport } from "@mcapps/shared/reportSchemas/akustikk/akustikkPremissrapportSchema";
import {
  setNodeActiveById,
  toggleChapterInTree,
} from "../../../../components/ReportChapterTree/reportChaptersTreeUtils";
import { setShowFailedMessageThunk, setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import { AppThunk } from "../../../../store";
import { makeTypedSetter } from "../makeTypedSetter";
import { doGetSingleMaconomyProject } from "../reportBoardAPI";
import { setEditedDataInRedux, setEditedValueInRedux } from "../reportSlice";
import { AKUSTIKK_DEPARTMENTS, applyAllAkustikkRules } from "./akustikkRapportUtils";

export const AKUSTIKK_RAPPORT_ID = 401;

export const setAkustikkValue = makeTypedSetter<IAkustikkPremissRapport, 401>(AKUSTIKK_RAPPORT_ID);

export const fetchMaconomyAndHydrateAkustikkGenerellInfoThunk = (): AppThunk => async (dispatch, getState) => {
  const project = getState().adminReducer.project;

  try {
    const multiconsultProject: IMulticonsultProject = await doGetSingleMaconomyProject(project._id);
    if (!multiconsultProject) {
      dispatch(setShowFailedMessageThunk("Ingen prosjektdata funnet i Maconomy"));
      return;
    }

    dispatch(setShowSuccessMessageThunk("Hentet data fra maconomy"));

    const ansvarligEnhet = AKUSTIKK_DEPARTMENTS.find((d) => d.startsWith(String(multiconsultProject.UnitId)));

    dispatch(
      setEditedValueInRedux({
        code: AKUSTIKK_RAPPORT_ID,
        path: "generellInfo.oppdragsleder",
        value: (multiconsultProject.ProjectManager as IMulticonsultUser)?.FullName ?? "",
      }),
    );
    dispatch(
      setEditedValueInRedux({
        code: AKUSTIKK_RAPPORT_ID,
        path: "generellInfo.oppdragsnavn",
        value: multiconsultProject.DisplayName ?? "",
      }),
    );
    dispatch(
      setEditedValueInRedux({
        code: AKUSTIKK_RAPPORT_ID,
        path: "generellInfo.oppdragsgiver",
        value: multiconsultProject.CustomerName ?? "",
      }),
    );
    dispatch(
      setEditedValueInRedux({
        code: AKUSTIKK_RAPPORT_ID,
        path: "generellInfo.kontaktperson",
        value: multiconsultProject.InvoiceName ?? "",
      }),
    );
    dispatch(
      setEditedValueInRedux({
        code: AKUSTIKK_RAPPORT_ID,
        path: "generellInfo.ansvarligEnhet",
        value: ansvarligEnhet ?? "",
      }),
    );
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  } catch (error) {
    dispatch(setShowFailedMessageThunk("Kunne ikke hente data fra maconomy"));
    throw error;
  }
};

export const thunkAkustikkKapittelToggled =
  (chapterId: string, next: boolean): AppThunk =>
  (dispatch, getState) => {
    const edited = getState().reportReducer.editedData[AKUSTIKK_RAPPORT_ID] as IAkustikkPremissRapport | null;
    if (!edited) return;

    // 1) user toggle
    let toggled = toggleChapterInTree(edited.kapitler, chapterId, next);

    if (next === true && chapterId === "vedleggC_1") {
      toggled = setNodeActiveById(toggled, "vedleggC_2", false);
    }

    if (next === true && chapterId === "vedleggC_2") {
      toggled = setNodeActiveById(toggled, "vedleggC_1", false);
    }

    const projectInformation = edited.prosjektinfo;

    // 2) rules (sticky) => returnerar både kapitler + toast-messages
    const ruleRes = applyAllAkustikkRules(projectInformation, toggled);

    // Ingen ändring => gör inget
    if (ruleRes.kapitler === edited.kapitler) return;

    // 3) skriv tillbaka uppdaterad data
    dispatch(
      setEditedDataInRedux({
        code: AKUSTIKK_RAPPORT_ID,
        data: {
          ...edited,
          kapitler: ruleRes.kapitler,
        },
      }),
    );
  };

export const toggleProsjektinfoAndApplyRules =
  (path: string, next: boolean): AppThunk =>
  (dispatch, getState) => {
    const edited = getState().reportReducer.editedData[AKUSTIKK_RAPPORT_ID] as IAkustikkPremissRapport | null;
    if (!edited) return;

    // IMPORTANT: assocPath returns a NEW object
    let nextEdited = R.assocPath(path.split("."), next, edited) as IAkustikkPremissRapport;

    if (
      path === "prosjektinfo.typeBygg.bolig.isActive" &&
      next === false &&
      nextEdited.prosjektinfo.typeBygg.bolig.flerFamiliehus.isActive === true
    ) {
      nextEdited = R.assocPath(
        ["prosjektinfo", "typeBygg", "bolig", "flerFamiliehus", "isActive"],
        false,
        nextEdited,
      ) as IAkustikkPremissRapport;
    }

    if (path === "prosjektinfo.vedlegg.tabellEllerTekst.isActive" && next === false) {
      nextEdited = R.assocPath(
        ["prosjektinfo", "vedlegg", "tabellEllerTekst", "tabellGrenseverdier", "isActive"],
        false,
        nextEdited,
      ) as IAkustikkPremissRapport;
      nextEdited = R.assocPath(
        ["prosjektinfo", "vedlegg", "tabellEllerTekst", "henvisningVedlegg", "isActive"],
        false,
        nextEdited,
      ) as IAkustikkPremissRapport;
    }

    if (path === "prosjektinfo.vedlegg.tabellEllerTekst.tabellGrenseverdier.isActive" && next === true) {
      nextEdited = R.assocPath(
        ["prosjektinfo", "vedlegg", "tabellEllerTekst", "isActive"],
        true,
        nextEdited,
      ) as IAkustikkPremissRapport;
      nextEdited = R.assocPath(
        ["prosjektinfo", "vedlegg", "tabellEllerTekst", "henvisningVedlegg", "isActive"],
        false,
        nextEdited,
      ) as IAkustikkPremissRapport;
    }

    if (path === "prosjektinfo.vedlegg.tabellEllerTekst.henvisningVedlegg.isActive" && next === true) {
      nextEdited = R.assocPath(
        ["prosjektinfo", "vedlegg", "tabellEllerTekst", "isActive"],
        true,
        nextEdited,
      ) as IAkustikkPremissRapport;
      nextEdited = R.assocPath(
        ["prosjektinfo", "vedlegg", "tabellEllerTekst", "tabellGrenseverdier", "isActive"],
        false,
        nextEdited,
      ) as IAkustikkPremissRapport;
    }

    const res = applyAllAkustikkRules(nextEdited.prosjektinfo, nextEdited.kapitler);

    dispatch(
      setEditedDataInRedux({
        code: AKUSTIKK_RAPPORT_ID,
        data: { ...nextEdited, kapitler: res.kapitler },
      }),
    );
  };
